import { useEffect, useState } from "react"
import ReactDOM from "react-dom"
import classnames from "classnames"

import If from "@mobi/components/If"
import Button from "../Button"

import "./Modal.scss"

import focusInElement from "@mobi/utils/browser/focusInElement"
import isClient from "@mobi/utils/browser/isClient"
import CloseIcon from "../../static/icons/outlined/fechar.svg"

const MODAL_ROOT_CLASS = "ds-modal-root"

interface IModalProps {
  /** Conteudo do Modal */
  children?: React.ReactNode
  /** Callback ao clicar no X para fechar o Modal */
  onCloseClick: () => void
  /** Controla o estado do modal, aberto ou fechado */
  show: boolean
  /** Callback de click do botão de estilo "primary" */
  primary?: {
    action: () => void
    label: string | JSX.Element
  } | null
  /** Callback de click do botão de estilo "secondary" */
  secondary?: {
    action: () => void
    label: string | JSX.Element
  } | null
  /** Define o tipo de modal que será utilizado */
  type: "center" | "pop"
  customClass: string
  /** Concatena classes a classe padrão ds-modal. */
  className?: string
}

const ariaHiddenSiblings = (ariaStatus, element) => {
  let sibling = element.parentNode.firstChild

  while (sibling) {
    if (sibling.nodeType === 1 && sibling !== element) {
      ariaStatus
        ? sibling.setAttribute("aria-hidden", ariaStatus)
        : sibling.removeAttribute("aria-hidden")
    }

    sibling = sibling.nextSibling
  }
}

const buildModalRoot = (rootId): HTMLElement => {
  const documentBody = document.body

  const divRoot = document.createElement("div")
  divRoot.id = rootId
  divRoot.classList.add(MODAL_ROOT_CLASS)

  documentBody.appendChild(divRoot)
  return divRoot
}

const generateModalId = () => {
  const hasOthersModals = document.getElementsByClassName("ds-modal-root")

  return hasOthersModals.length > 0
    ? `${MODAL_ROOT_CLASS}-${hasOthersModals.length + 1}`
    : `${MODAL_ROOT_CLASS}-1`
}

const Modal = ({
  children,
  onCloseClick,
  show = false,
  className = "",
  primary,
  secondary,
  type,
  customClass = ""
}: IModalProps) => {
  if (!isClient()) return null
  const [displayState, setDisplay] = useState(false)
  const [visibleState, setVisibleState] = useState(false)
  const [rootElement, setRootElement] = useState(document.body)
  const [rootId, setRootId] = useState("")

  const modalBaseClass = "ds-modal"

  const modalClasses = classnames(modalBaseClass, className, {
    [`${modalBaseClass}--${type}`]: !!type,
    [`${modalBaseClass}--show`]: visibleState,
  })

  const openModal = () => {
    const rootId = generateModalId()
    const rootElement = buildModalRoot(rootId)

    document.body.style.overflow = "hidden"

    setRootElement(rootElement)
    setRootId(rootId)
    ariaHiddenSiblings(true, rootElement)
    setDisplay(true)

    setTimeout(() => {
      setVisibleState(true)
      focusInElement(`.${modalBaseClass}-container__close`, { timeout: 400 })
    }, 150)
  }

  const closeModal = (): void => {
    const rootToRemove = document.getElementById(rootId)

    if (rootToRemove !== null) {
      setVisibleState(false)
      ariaHiddenSiblings(false, rootElement)

      setTimeout(() => {
        setDisplay(false)
        rootToRemove.remove()
        document.body.style.removeProperty("overflow")
      }, 400)
    }
  }

  useEffect(() => {
    show ? openModal() : closeModal()
  }, [show])

  const ModalElement = (
    <If
      condition={displayState}
      renderIf={
        <div
          className={modalClasses}
          role="dialog"
          tabIndex={1}
          onKeyPress={() => onCloseClick}
        >
          <div className={`${modalBaseClass}-container ${customClass}`}>
            <div className={`${modalBaseClass}-container__close`}>
              <Button
                className={`${modalBaseClass}-container__close-button`}
                onClick={onCloseClick}
                variant="high-contrast"
                aria-label="Fechar Modal"
              >
                <CloseIcon width={25} height={25} />
              </Button>
            </div>
            <div className={`${modalBaseClass}-container__body`}>
              {children}
            </div>

            <If
              condition={!!primary || !!secondary}
              renderIf={
                <div className={`${modalBaseClass}-container__footer`}>
                  <If
                    condition={!!secondary}
                    renderIf={
                      <Button
                        onClick={secondary ? secondary.action : null}
                        variant="secondary"
                        fluid
                        aria-label={secondary ? secondary.label : ""}
                      >
                        {secondary ? secondary.label : ""}
                      </Button>
                    }
                  />

                  <If
                    condition={!!primary}
                    renderIf={
                      <Button
                        onClick={primary ? primary.action : null}
                        variant="primary"
                        fluid
                        aria-label={primary ? primary.label : ""}
                      >
                        {primary ? primary.label : ""}
                      </Button>
                    }
                  />
                </div>
              }
            />
          </div>
        </div>
      }
    />
  )

  return ReactDOM.createPortal(ModalElement, rootElement)
}

export default Modal
